import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import MapView from '@arcgis/core/views/MapView';

type UseArcgisSketchLayerArgs = {
  mapView: MapView | null;
  layerTitle: string;
  onCreateComplete?: (args: {
    geometry: __esri.GeometryUnion;
    sketchViewModel: SketchViewModel;
  }) => void;
};

/**
 * Manages a layer for drawn objects on the map, and a sketch view model for the
 * drawing operations.
 */
export const useArcgisSketchLayer = ({
  mapView,
  layerTitle,
  onCreateComplete,
}: UseArcgisSketchLayerArgs) => {
  const onCreateCompleteRef = useRef(onCreateComplete);
  const drawingLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: layerTitle })
  );
  const sketchViewModelRef = useRef<SketchViewModel | null>(null);

  useLayoutEffect(() => {
    onCreateCompleteRef.current = onCreateComplete;
  }, [onCreateComplete]);

  useEffect(() => {
    if (!mapView?.map) return;

    const map = mapView.map;
    const drawingLayer = drawingLayerRef.current;
    map.add(drawingLayer);

    const sketchViewModel = new SketchViewModel({
      view: mapView,
      layer: drawingLayer,
      updateOnGraphicClick: false,
    });
    sketchViewModelRef.current = sketchViewModel;

    const createHandle = sketchViewModel.on('create', (event) => {
      if (event.state !== 'complete' || !event.graphic?.geometry) return;

      onCreateCompleteRef.current?.({
        geometry: event.graphic.geometry,
        sketchViewModel,
      });
    });

    return () => {
      createHandle.remove();
      sketchViewModel.destroy();
      sketchViewModelRef.current = null;
      map.remove(drawingLayer);
    };
  }, [mapView]);

  const clearDrawingLayer = useCallback(() => {
    drawingLayerRef.current.removeAll();
  }, []);

  const cancelActiveDrawing = useCallback(() => {
    if (sketchViewModelRef.current?.state === 'active') {
      sketchViewModelRef.current.cancel();
    }
  }, []);

  return {
    drawingLayerRef,
    sketchViewModelRef,
    clearDrawingLayer,
    cancelActiveDrawing,
  };
};
